import Component from '@glimmer/component';
import { on } from '@ember/modifier';
import Heading from 'tio-ui/components/heading';
import Text from 'tio-ui/components/text';
import Button from 'tio-ui/components/button';
import {
  PhArrowRight,
  PhWarning,
  PhBriefcase,
  PhBank,
  PhHouse,
  PhRepeat,
} from 'ember-phosphor-icons';
import type { PslfScenario } from 'tio-employee/services/career-pathways-api';

interface ScenarioCardSignature {
  Args: {
    scenario: PslfScenario;
    onExplore?: (scenarioType: string) => void;
  };
  Element: HTMLDivElement;
}

// Get icon based on scenario type
function getScenarioIcon(type: string): typeof PhBriefcase {
  switch (type) {
    case 'leave_public_service':
      return PhBriefcase;
    case 'refinance':
      return PhBank;
    case 'change_employer':
      return PhHouse;
    case 'change_plan':
      return PhRepeat;
    default:
      return PhBriefcase;
  }
}

// Map the data-layer recommendation onto a TIO status intent + label.
// Off-palette legacy mapping was `do_not → red`, `caution → amber`,
// `consider → blue`, `safe → emerald`; re-homed onto our four status
// intents (danger / warning / info / success) so a single .app-*
// recipe family paints the entire severity pill + header tile.
type SeverityIntent = 'danger' | 'warning' | 'info' | 'success' | 'neutral';
function getRecommendationSeverity(rec: PslfScenario['recommendation']): {
  intent: SeverityIntent;
  label: string;
} {
  switch (rec) {
    case 'do_not':
      return { intent: 'danger', label: 'Not Recommended' };
    case 'caution':
      return { intent: 'warning', label: 'Use Caution' };
    case 'consider':
      return { intent: 'info', label: 'Consider Carefully' };
    case 'safe':
      return { intent: 'success', label: 'Safe Option' };
    default:
      return { intent: 'neutral', label: 'Unknown' };
  }
}

export default class PslfScenarioCard extends Component<ScenarioCardSignature> {
  get Icon() {
    return getScenarioIcon(this.args.scenario.type);
  }

  get severity() {
    return getRecommendationSeverity(this.args.scenario.recommendation);
  }

  get isWarning() {
    return (
      this.args.scenario.recommendation === 'do_not' ||
      this.args.scenario.recommendation === 'caution'
    );
  }

  /**
   * Class set for the leading header icon-tile. `do_not`/`caution`
   * scenarios paint with the danger wash; everything else uses the
   * info wash. Returns the `.app-icon-tile` chrome + the intent
   * modifier — see addons/tio-ui/src/styles.css.
   */
  get headerIconClass() {
    return this.isWarning
      ? 'app-icon-tile app-icon-tile--danger'
      : 'app-icon-tile app-icon-tile--info';
  }

  /**
   * Class set for the severity pill in the card header. Maps the
   * data-layer intent onto the matching `.app-severity-pill--*` recipe.
   */
  get severityPillClass() {
    const { intent } = this.severity;
    if (intent === 'neutral') return 'app-severity-pill';
    return `app-severity-pill app-severity-pill--${intent}`;
  }

  get formattedForgivenessLoss() {
    const amount = this.args.scenario.impact.forgivenessLost;
    if (amount === 0) return '$0';
    return `-$${amount.toLocaleString()}`;
  }

  get formattedAdditionalCost() {
    const cost = this.args.scenario.impact.additionalCost;
    if (cost === 0) return '$0';
    return `+$${cost.toLocaleString()}`;
  }

  handleExplore = () => {
    this.args.onExplore?.(this.args.scenario.type);
  };

  <template>
    {{! template-lint-disable no-bare-strings }}
    <div
      class="tio-bg-surface rounded-xl p-5 border tio-border-default hover:tio-border-strong hover:shadow-md transition-all"
      ...attributes
    >
      {{! Header }}
      <div class="tio-row-start-between gap-3 mb-4">
        <div class="tio-row min-w-0">
          <div class={{this.headerIconClass}}>
            <this.Icon @size={{24}} />
          </div>
          <Heading @level={{3}}>
            {{@scenario.title}}
          </Heading>
        </div>
        <div class={{this.severityPillClass}}>
          {{this.severity.label}}
        </div>
      </div>

      {{! Description }}
      <Text @size="sm" class="tio-fg-secondary mb-4">
        {{@scenario.description}}
      </Text>

      {{! Impact Analysis }}
      <div class="app-sunken-card rounded-lg p-4 mb-4">
        <Text @size="xs" class="tio-fg-muted uppercase tracking-wide mb-3">
          Impact Analysis
        </Text>

        <div class="grid grid-cols-3 gap-3">
          {{! Forgiveness Lost }}
          <div class="min-w-0">
            <Text @size="xs" class="tio-fg-muted mb-1">Forgiveness Lost</Text>
            <Text @size="sm" @weight="bold" class="app-status-text-danger">
              {{this.formattedForgivenessLoss}}
            </Text>
          </div>

          {{! Additional Cost }}
          <div class="min-w-0">
            <Text @size="xs" class="tio-fg-muted mb-1">Extra Cost</Text>
            <Text @size="sm" @weight="bold" class="app-status-text-warning">
              {{this.formattedAdditionalCost}}
            </Text>
          </div>

          {{! Time Impact }}
          <div class="min-w-0">
            <Text @size="xs" class="tio-fg-muted mb-1">Timeline</Text>
            <Text @size="sm" @weight="bold" class="tio-fg-secondary">
              {{@scenario.impact.timeImpact}}
            </Text>
          </div>
        </div>
      </div>

      {{! Warning message }}
      {{#if this.isWarning}}
        <div
          class="tio-row-top-tight p-3 app-status-panel app-status-panel--danger rounded-lg mb-4"
        >
          <PhWarning @size={{18}} class="app-icon-danger shrink-0 mt-0.5" />
          <Text @size="sm" class="app-status-text-danger">
            {{@scenario.message}}
          </Text>
        </div>
      {{/if}}

      {{! Details }}
      {{#if @scenario.details.length}}
        <div class="mb-4">
          <Text @size="xs" class="tio-fg-muted uppercase tracking-wide mb-2">Key Factors</Text>
          <ul class="space-y-1">
            {{#each @scenario.details as |detail|}}
              <li class="tio-fg-secondary text-sm tio-row-tight">
                <span class="w-1.5 h-1.5 bg-gray-400 dark:bg-gray-500 rounded-full shrink-0"></span>
                {{detail}}
              </li>
            {{/each}}
          </ul>
        </div>
      {{/if}}

      {{! Explore Button }}
      {{#if @onExplore}}
        <Button
          @intent="primary"
          @appearance="outlined"
          class="w-full justify-center"
          {{on "click" this.handleExplore}}
        >
          <span>Explore this scenario</span>
          <PhArrowRight @size={{16}} />
        </Button>
      {{/if}}
    </div>
  </template>
}
